import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import { useIsMobile } from '../lib/useIsMobile';
const brands = [{
  name: 'CyberGhost VPN',
  logo: 'https://cdn.magicpatterns.com/uploads/syzFbVJY22Utjn1RPAgZRC/pngegg_(3).png'
}, {
  name: 'Skillshare',
  logo: 'https://cdn.magicpatterns.com/uploads/8vzpwh9URL1VmxTV2htyev/skillshare_logo_icon_249611.png'
}, {
  name: 'Coupert',
  logo: 'https://cdn.magicpatterns.com/uploads/xgxDzASPQCrKLwWrbRK853/coupert-seeklogo.png'
}, {
  name: 'Lingoda',
  logo: 'https://cdn.magicpatterns.com/uploads/mWHA5x6byMK9JEDdN5Xtps/lingoda-seeklogo.png'
}];
const leftColumnBrands = [...brands];
const rightColumnBrands = [brands[2], brands[0], brands[3], brands[1]];
const leftLoop = [...leftColumnBrands, ...leftColumnBrands, ...leftColumnBrands];
const rightLoop = [...rightColumnBrands, ...rightColumnBrands, ...rightColumnBrands];
function BrandCard({
  brand
}: {
  brand: (typeof brands)[0];
}) {
  return <div className="flex items-center justify-center p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-3xl bg-white border-3 sm:border-4 border-gray-800 aspect-[4/3] shrink-0 shadow-[4px_4px_0px_rgba(255,211,61,0.3)] sm:shadow-[6px_6px_0px_rgba(255,211,61,0.3)] hover:shadow-[6px_6px_0px_rgba(255,211,61,0.4)] sm:hover:shadow-[8px_8px_0px_rgba(255,211,61,0.4)] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-300" data-id="element-29">
      <img src={brand.logo} alt={brand.name} className="max-h-8 sm:max-h-12 md:max-h-16 max-w-[80%] w-auto h-auto object-contain" loading="lazy" data-id="element-30" />
    </div>;
}
export function BrandsMarquee() {
  const isMobile = useIsMobile();
  const bounceTransition = {
    type: 'spring',
    stiffness: 260,
    damping: 20
  };
  return <section className="relative z-10 py-16 sm:py-20 md:py-32 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-31">
      <div className="relative p-6 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-purple-100 to-pink-100 border-3 sm:border-4 border-gray-800 shadow-[8px_8px_0px_rgba(167,139,250,0.3)] sm:shadow-[16px_16px_0px_rgba(167,139,250,0.3)]" data-id="element-32">
        {/* Decorative stars */}
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 flex gap-1 sm:gap-2" data-id="element-33">
          <Star className="w-4 h-4 sm:w-6 sm:h-6 fill-yellow-400 text-yellow-400 animate-wiggle" data-id="element-34" />
          <Star className="w-4 h-4 sm:w-6 sm:h-6 fill-yellow-400 text-yellow-400 animate-bounce-gentle" data-id="element-35" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center" data-id="element-36">
          {/* Left Content */}
          <motion.div initial={{
          x: -50,
          opacity: 0
        }} whileInView={{
          x: 0,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={bounceTransition} data-id="element-37">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-fredoka font-bold text-gray-800 mb-4 sm:mb-6 leading-tight" data-id="element-38">
              Trusted by leading brands worldwide
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 font-medium leading-relaxed" data-id="element-39">
              We partner with forward-thinking brands to create campaigns that
              resonate. The next success story could be yours.
            </p>
            <Link to="/brands" className="btn-playful" data-id="element-40">
              Partner With Us
            </Link>
          </motion.div>

          {/* Right Content - Scrolling Columns */}
          <div className="relative h-[300px] sm:h-[400px] md:h-[500px] overflow-hidden rounded-xl sm:rounded-2xl" data-id="element-41">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6 h-full" data-id="element-42">
              {/* Left column - scrolls UP */}
              <div className="overflow-hidden" data-id="element-43">
                <motion.div className="flex flex-col gap-3 sm:gap-4 md:gap-6" animate={{
                y: ['0%', '-33.33%']
              }} transition={{
                y: {
                  duration: isMobile ? 20 : 15,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatType: 'loop'
                }
              }} data-id="element-44">
                  {leftLoop.map((brand, index) => <BrandCard key={`left-${index}`} brand={brand} data-id="element-45" />)}
                </motion.div>
              </div>

              {/* Right column - scrolls DOWN */}
              <div className="overflow-hidden" data-id="element-46">
                <motion.div className="flex flex-col gap-3 sm:gap-4 md:gap-6" initial={{
                y: '-33.33%'
              }} animate={{
                y: ['-33.33%', '0%']
              }} transition={{
                y: {
                  duration: isMobile ? 20 : 15,
                  repeat: Infinity,
                  ease: 'linear',
                  repeatType: 'loop'
                }
              }} data-id="element-47">
                  {rightLoop.map((brand, index) => <BrandCard key={`right-${index}`} brand={brand} data-id="element-48" />)}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}